/**
 * Фишка: числа маркера, рисунок и вид на `Stage`. Блок не знает движка: сцену он получает через `Stage`
 * (`engines/base.ts`). Размер фишки задан в ширинах клетки, ширину клетки сообщает тот, кто её ставит (`cell`).
 */

import {
  Box,
  Cache,
  Drawing,
  Img,
  Item,
  Point,
  Stage,
  SymbolImage,
  Tex,
  density,
  drawing,
  radialDrawing,
  rgba,
  shadowDrawing,
  symbolImage,
  Resources,
} from '../engines/base.js'
import { AVATAR_SHEET, avatarFor } from './avatars.js'
import { PawnId } from '../../srv/protocol.js'

// === фишка: числа ==========================================================
/*
 * Фишка игрока как компонент: числа маркера в его кадре (`PAWN_FRAME`, единицы 1200×2074), общие для
 * всех холстов. Макет читается как есть: капля цвета игрока, окно с аватаркой, ободок, плашка с именем,
 * свечение активного и тень. Способ рисовать — у реализации (`pawn.ts`).
 */

/** Как выглядит фишка. Цвет — имя из палитры `index.main.css` (оно же имя фишки), а не значение (`R106`). */
export interface PawnLook {
  avatar: string
  color: PawnId
  /** Подпись под фишкой; пусто — без подписи. */
  name?: string
}

/**
 * Бокс фишки в долях поля: остриё в точке покоя `spot`, ширина — `scale` ширин клетки (`cell`), высота по
 * кадру. `k` — пикселей на единицу поля, ноль в начале координат поля.
 */
export function pawnBox(cell: number, spot: Point, scale: number, k: number): Box {
  const w = cell * scale * k
  return {
    x: spot.x * k - w / 2,
    y: spot.y * k - (w * (PAWN_FRAME.tip - PAWN_FRAME.y)) / PAWN_FRAME.w,
    w,
    h: (w * PAWN_FRAME.h) / PAWN_FRAME.w,
  }
}

/** Размер фишки в ширинах клетки: на одной клетке их может стоять четыре. */
export const PAWN_SCALE = 2

/**
 * Кадр фишки: окно рисунка `x y w h` в единицах самого маркера и `tip` — где в этом кадре остриё
 * булавки, y. Фишка стоит остриём в точке покоя; по этим числам её ставит сцена, и они же нужны
 * любой реализации, чтобы сказать, где фишка на экране. Тот же кадр рисует и разметка (`pawn.markup.tsx`).
 */
export const PAWN_FRAME = { x: 0, y: -244, w: 1200, h: 2074, tip: 1740 } as const

export const PAWN_WELL = { cx: 600, cy: 850, r: 340 }
export const PAWN_PLATE = { h: 334, pad: 110, bottom: 100 }
export const PAWN_NAME = { size: 180, track: 23.6 }
export const PAWN_SHADOW = { cx: 600, cy: 1760, rx: 260, ry: 60 }
export const PAWN_GLOW = { r: Math.round(PAWN_WELL.r * 2.1), tex: 256 }
/** Свет разгорается и гаснет за 320 мс (`--player-glow-ms`). */
const PAWN_GLOW_MS = 320

/** Капля маркера: сдвиг в кадре, начало контура и кубические кривые (`x1 y1 x2 y2 x y`). */
const PAWN_PIN = {
  at: { x: 150, y: 400 },
  start: { x: 450, y: 1340 },
  curves: [
    [450, 1340, 0, 760, 0, 450],
    [0, 201.47, 201.47, 0, 450, 0],
    [698.53, 0, 900, 201.47, 900, 450],
    [900, 760, 450, 1340, 450, 1340],
  ] as ReadonlyArray<readonly [number, number, number, number, number, number]>,
}

/** Всё, от чего рисунок фишки зависит: сменился ключ — фишку пересобирают. `px` — пикселей на единицу кадра. */
export const pawnKey = (look: PawnLook, px: number): string =>
  `${look.avatar}|${look.color}|${look.name ?? ''}|${px.toFixed(4)}`

// === фишка: рисунки ========================================================

/** Свечение активного игрока: белое пятно, гаснущее к краю. */
const glowDrawing = (): Drawing =>
  radialDrawing(PAWN_GLOW.tex, [
    [0, 'rgba(255,255,255,1)'],
    [0.45, 'rgba(255,255,255,.5)'],
    [0.75, 'rgba(255,255,255,.26)'],
    [1, 'rgba(255,255,255,0)'],
  ])

/**
 * Тело фишки одним холстом в кадре маркера (`PAWN_FRAME`): капля цвета игрока, окно, аватарка, ободок и плашка с именем.
 * Тень и свечение сюда не входят: они меняются по ходу, а тело нет. `px` — экранных пикселей на единицу кадра, `dpr` —
 * плотность экрана: от них зависят толщина ободка (один экранный пиксель) и кегль имени; `symbol` — аватарка, если доехала.
 */
function pawnBodyDrawing(
  look: PawnLook,
  symbol: SymbolImage | null,
  px: number,
  dpr: number,
  css: Resources['color'],
): Drawing {
  const s = px * dpr
  const color = css(`--color-marker-${look.color}`)
  return drawing(Math.ceil(PAWN_FRAME.w * s), Math.ceil(PAWN_FRAME.h * s), (c) => {
    c.scale(s, s)
    c.translate(-PAWN_FRAME.x, -PAWN_FRAME.y)
    const { cx, cy, r } = PAWN_WELL
    c.save()
    c.translate(PAWN_PIN.at.x, PAWN_PIN.at.y)
    c.beginPath()
    c.moveTo(PAWN_PIN.start.x, PAWN_PIN.start.y)
    for (const [a, b, d, e, x, y] of PAWN_PIN.curves) c.bezierCurveTo(a, b, d, e, x, y)
    c.closePath()
    c.fillStyle = color
    c.fill()
    c.restore()
    c.beginPath()
    c.arc(cx, cy, r, 0, Math.PI * 2)
    c.fillStyle = rgba(css('--color-deep'), 0.6)
    c.fill()
    if (symbol) {
      c.save()
      c.beginPath()
      c.arc(cx, cy, r, 0, Math.PI * 2)
      c.clip()
      c.drawImage(symbol.img, cx - r, cy - r, r * 2, r * 2)
      c.restore()
    }
    c.beginPath()
    c.arc(cx, cy, r, 0, Math.PI * 2)
    c.lineWidth = 1 / px || 1
    c.strokeStyle = '#fff'
    c.stroke()
    if (!look.name) return
    // Плашка обнимает имя; кегль не меньше шести экранных пикселей, разрядка после последней буквы не считается.
    c.font = `700 ${Math.max(6, PAWN_NAME.size * px) / px}px system-ui, sans-serif`
    c.textBaseline = 'middle'
    const letters = [...look.name]
    const widths = letters.map((l) => c.measureText(l).width)
    const text = widths.reduce((a, w) => a + w, 0) + PAWN_NAME.track * (letters.length - 1)
    const width = text + 2 * PAWN_PLATE.pad
    const y = PAWN_PLATE.bottom - PAWN_PLATE.h / 2
    c.beginPath()
    c.roundRect(
      (PAWN_FRAME.w - width) / 2,
      PAWN_PLATE.bottom - PAWN_PLATE.h,
      width,
      PAWN_PLATE.h,
      PAWN_PLATE.h / 2,
    )
    c.fillStyle = color
    c.fill()
    c.fillStyle = css('--color-deep')
    let x = (PAWN_FRAME.w - text) / 2
    letters.forEach((l, i) => {
      c.fillText(l, x, y)
      x += widths[i] + PAWN_NAME.track
    })
  })
}

/** Загрузчик аватарок набора 0 с кэшем: аватарка — картинка, на любом размере рисуется заново из вектора. */
function avatarLoader(sheet: Resources['sheet']): (name: string) => Promise<SymbolImage> {
  const avatars = new Map<string, Promise<SymbolImage>>()
  return (name) => {
    let symbol = avatars.get(name)
    if (!symbol)
      avatars.set(
        name,
        (symbol = sheet(AVATAR_SHEET).then((text) => symbolImage(text, `avatar-0-${name}`, 1.4))),
      )
    return symbol
  }
}

// === фишка на сцене ========================================================
/*
 * Фишка игрока на сцене: тот же маркер, что `Player` в `pawn.markup.tsx` — капля цвета игрока, окно с аватаркой, ободок,
 * плашка с именем, свечение активного и тень.
 *
 * Рисуется в кадре маркера (`PAWN_FRAME`, единицы 1200×2074), числа общие (`pawn.ts`); ставит и масштабирует фишку
 * движок через `place`.
 */

/** Сколько тел фишек держать про запас: фишек до шести, текущий размер и предыдущий. */
const BODIES_MAX = 12

/** Текстуры, общие для всех фишек одного движка: тень, свечение, тела фишек; аватарки по требованию. */
export class PawnArt {
  readonly shadow: Tex
  readonly glow: Tex
  /** Тела фишек по ключу: фишку, которую пересоздали на том же размере, берут снова, а не рисуют. */
  private readonly bodies = new Cache<Tex>(BODIES_MAX, (tex) => tex.release())

  constructor(
    readonly stage: Stage,
    /** Ширина клетки поля, единицы поля: фишка стоит размером в `PAWN_SCALE` клеток. */
    readonly cell: number,
    readonly resources: Pick<Resources, 'sheet' | 'color'>,
  ) {
    this.avatar = avatarLoader(resources.sheet)
    this.shadow = stage.texture(shadowDrawing(), true)
    this.glow = stage.texture(glowDrawing(), true)
  }

  /** Тело фишки; `draw` зовётся, только если такого ещё нет. */
  body(key: string, draw: () => Drawing): Tex {
    return this.bodies.get(key, () => this.stage.texture(draw()))
  }

  /** Движок закрывается: тела уйдут вместе с ним. */
  release(): void {
    this.bodies.clear()
  }

  /** Аватарка набора 0 как картинка. */
  readonly avatar: (name: string) => Promise<SymbolImage>
}

export class ScenePawn {
  readonly root: Item
  private readonly shadow: Img
  private readonly glow: Img
  private readonly body: Img
  private lit = 0
  private wantLit: boolean
  private dead = false
  private readonly cell: number

  /**
   * `px` — экранных пикселей на единицу кадра маркера: по нему ободок остаётся в один пиксель, а имя рисуется кеглем в
   * пикселях экрана и остаётся чётким. Смена размера — новая фишка (`pawnKey`). Тело фишки (капля, окно, аватарка,
   * ободок, плашка) — один холст, сделанный один раз (`pawnBodyDrawing`); по ходу меняются только место, тень и свет.
   */
  constructor(
    art: PawnArt,
    look: PawnLook,
    lit: boolean,
    seat: number,
    px: number,
    /** Аватарка доехала: сцене пора перерисоваться. */
    onReady: () => void,
  ) {
    const stage = art.stage
    this.cell = art.cell
    this.wantLit = lit
    // Уже горящая фишка не разгорается заново: её пересобрали из-за размера, а не из-за хода.
    this.lit = lit ? 1 : 0
    this.shadow = stage
      .img(art.shadow)
      .fit(PAWN_SHADOW.rx * 2, PAWN_SHADOW.ry * 2)
      .at(PAWN_SHADOW.cx, PAWN_SHADOW.cy)
    this.glow = stage.img(art.glow).at(PAWN_WELL.cx, PAWN_WELL.cy)
    this.glow.alpha = this.lit
    this.body = stage.img(undefined, 'corner').at(PAWN_FRAME.x, PAWN_FRAME.y)
    this.body.visible = false
    const inner = stage.group(this.shadow, this.glow, this.body).at(-PAWN_FRAME.x, -PAWN_FRAME.y)
    this.root = stage.group(inner)
    this.root.scale = (this.cell * PAWN_SCALE) / PAWN_FRAME.w

    // Тело появляется, когда аватарка доехала: оно рисуется целиком и не перерисовывается.
    const dpr = density()
    const avatar = avatarFor(look.avatar, seat)
    void art.avatar(avatar).then((symbol) => {
      if (this.dead) return
      const tex = art.body(`${pawnKey(look, px)}|${avatar}|${dpr}`, () =>
        pawnBodyDrawing(look, symbol, px, dpr, art.resources.color),
      )
      this.body.tex = tex
      this.body.fit(tex.width / (px * dpr), tex.height / (px * dpr))
      this.body.visible = true
      onReady()
    })
  }

  setLit(on: boolean): void {
    this.wantLit = on
  }

  /** Фишка острием в `spot` (единицы поля), на высоте `lift`: тень остаётся на поверхности и мельчает. */
  place(spot: Point, lift = 0): void {
    const at = pawnBox(this.cell, spot, PAWN_SCALE, 1)
    this.root.at(at.x, at.y)
    const k = 1 / (1 + lift / this.cell)
    this.shadow.at(PAWN_SHADOW.cx, PAWN_SHADOW.cy + (lift * PAWN_FRAME.w) / (this.cell * PAWN_SCALE))
    this.shadow.fit(k * PAWN_SHADOW.rx * 2, k * PAWN_SHADOW.ry * 2)
    this.shadow.alpha = k
  }

  /** Свет разгорается и гаснет плавно. Возвращает, продолжается ли переход. */
  update(dt: number): boolean {
    const target = this.wantLit ? 1 : 0
    if (this.lit !== target) {
      this.lit = Math.min(1, Math.max(0, this.lit + (dt / PAWN_GLOW_MS) * (target ? 1 : -1)))
      if (Math.abs(this.lit - target) < 1e-6) this.lit = target
    }
    this.glow.alpha = this.lit
    this.glow.fit((this.lit * 0.35 + 0.65) * PAWN_GLOW.r * 2)
    return this.lit !== target
  }

  destroy(): void {
    this.dead = true
    this.root.destroy()
  }
}
